// src/contexts/fiscal/MDFeContext.js
import React, { createContext, useContext, useMemo, useState } from 'react';
import * as service from '../../services/fiscal/MDFeService';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';

const MDFeContext = createContext();
export const useMDFe = () => useContext(MDFeContext);

const extractErrorMessage = (error) => {
  if (error?.errors && typeof error.errors === 'object') {
    return Object.entries(error.errors)
      .flatMap(([field, messages]) => {
        const list = Array.isArray(messages) ? messages : [messages];
        return list.map((message) => `${field}: ${message}`);
      })
      .join('\n');
  }

  return error?.title || error?.message || String(error);
};

export const MDFeProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const { showAlert } = useAlert();
  const { showMessage } = useMessage();

  const executar = async (acao, mensagem) => {
    setLoading(true);
    try {
      const result = await acao();
      if (mensagem) showAlert(mensagem);
      return result;
    } catch (error) {
      console.error('Erro ao processar MDF-e:', error);
      showMessage(`Não foi possível processar o MDF-e.\n${extractErrorMessage(error)}`, 'danger');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(() => ({
    loading,
    listar: (filtros) => executar(() => service.listarMDFe(filtros)),
    obter: (id) => executar(() => service.obterMDFe(id)),
    criar: (payload) => executar(() => service.criarMDFe(payload), 'Rascunho do MDF-e salvo com sucesso!'),
    atualizar: (id, payload) => executar(() => service.atualizarMDFe(id, payload), 'Rascunho do MDF-e atualizado com sucesso!'),
    emitir: (id) => executar(() => service.emitirMDFe(id)),
    consultar: (id) => executar(() => service.consultarMDFe(id), 'Situação consultada com sucesso!'),
    excluir: (id) => executar(() => service.excluirMDFe(id), 'Rascunho excluído com sucesso!'),
    cancelar: (id, justificativa) => executar(() => service.cancelarMDFe(id, justificativa), 'MDF-e cancelado com sucesso!'),
    encerrar: (id, payload) => executar(() => service.encerrarMDFe(id, payload), 'MDF-e encerrado com sucesso!'),
    incluirCondutor: (id, payload) => executar(() => service.incluirCondutorMDFe(id, payload), 'Condutor incluído com sucesso!'),
    buscarNotas: (filialId) => executar(() => service.buscarNFeAutorizadas(filialId)),
    baixarXml: (id) => executar(async () => service.salvarBlob(await service.baixarXmlMDFe(id), `mdfe-${id}.xml`)),
    abrirDamdfe: (id) => executar(async () => service.salvarBlob(await service.baixarDamdfe(id), `damdfe-${id}.pdf`, true)),
  }), [loading]);

  return <MDFeContext.Provider value={value}>{children}</MDFeContext.Provider>;
};
